import React, {
  useContext,
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  useTheme,
} from "@mui/material";
import { Plus, ChevronLeft } from "lucide-react";
import { EmptyState } from "@/modules/common/components/ui-kit/empty-state";
import DeveloperTrackIcon from "@/assets/icons/lead-page/developer-track";
import DeveloperTrackCard from "./developer-track-card";
import DeveloperTrackSkeleton from "./developer-track-skeleton";
import TrackActivity from "./track-activity";
import { DeveloperTrack } from "./types";
import ArchivedOverlay from "@/modules/common/components/ui-kit/archived-overlay";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import EditDeveloperUserModal from "./modals/edit-developer-user-modal";
import UpdateStatusModal from "./modals/update-status-modal";
import useEditDeveloperUser from "./hooks/use-edit-developer-user.hook";
import useUpdateTrackStatus from "./hooks/use-update-track-status.hook";
import { useLeadTracks } from "./hooks/use-lead-tracks.hook";
import { transformLeadTrackToUI } from "./utils/transformers";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import CreateTrackModal from "./modals/create-track-modal";
import { useSearchParams } from "react-router-dom";

interface DeveloperTracksTabProps {
  // Mobile-specific props for handling track selection
  onMobileTrackSelect?: (track: DeveloperTrack) => void;
  selectedMobileTrack?: DeveloperTrack | null;
}

interface FilterChipProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
}

interface CardPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

const FilterChip: React.FC<FilterChipProps> = ({
  label,
  isSelected,
  onClick,
}) => {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "36px",
        padding: `${theme.nawy.spacing.xs} ${theme.nawy.spacing.md}`,
        borderRadius: theme.nawy.radius.lg,
        border: `1px solid ${
          isSelected
            ? theme.nawy.colors.semantic.border.accent
            : theme.nawy.colors.semantic.border.primary
        }`,
        backgroundColor: isSelected
          ? theme.nawy.colors.semantic.layer.accentLight
          : theme.nawy.colors.semantic.layer.primary,
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: isSelected
            ? theme.nawy.colors.semantic.layer.accentLight
            : theme.nawy.colors.semantic.layer.secondary,
          borderColor: isSelected
            ? theme.nawy.colors.semantic.border.accent
            : theme.nawy.colors.semantic.border.primaryDark,
        },
      }}
    >
      <Typography
        sx={{
          ...theme.nawy.typography.english.variants.body.md.regular,
          color: isSelected
            ? theme.nawy.colors.semantic.text.accent
            : theme.nawy.colors.semantic.text.primary,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const DeveloperTracksTab: React.FC<DeveloperTracksTabProps> = ({
  onMobileTrackSelect,
  selectedMobileTrack,
}) => {
  const { lead } = useContext(LeadPageContext);
  const isLeadArchived = lead?.data?.lead?.archived;
  const leadUid = lead?.data?.lead?.uid;
  const { isMobile } = useScreenSize();
  const theme = useTheme();

  // Check if the lead type allows developer tracks
  // The flag is expected to be has_developer_tracks or hasDeveloperTracks on leadType
  const leadType = lead?.data?.lead?.leadType as any;
  const hasDeveloperTracks = leadType?.has_developer_tracks ?? leadType?.hasDeveloperTracks ?? false;
  
  const canAddTrack =hasDeveloperTracks;

  const [selectedDeveloper, setSelectedDeveloper] =
    useState<string>("Overview");

  // State for detail view with animation
  const [selectedTrack, setSelectedTrack] = useState<DeveloperTrack | null>(
    null
  );
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [startPosition, setStartPosition] = useState<CardPosition | null>(null);

  // Modal state for track being edited
  const [trackForEdit, setTrackForEdit] = useState<DeveloperTrack | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Fetch tracks from API
  const {
    data: tracksData,
    isLoading,
    isError,
  } = useLeadTracks({
    leadUId: leadUid || "",
    enabled: !!leadUid,
  });

  // Transform API data to UI types
  const tracks: DeveloperTrack[] = useMemo(() => {
    if (!tracksData?.results) return [];
    return tracksData.results.map(transformLeadTrackToUI);
  }, [tracksData]);

  // Extract unique developer names from tracks for filter chips
  const developers: string[] = useMemo(() => {
    const uniqueDevelopers = [...new Set(tracks.map((t) => t.developerName))];
    // Filter out "Unknown Developer" from the chips
    return uniqueDevelopers.filter((name) => name !== "Unknown Developer");
  }, [tracks]);

  // Auto-open track detail view from URL param (e.g., navigated from activity)
  const [searchParams, setSearchParams] = useSearchParams();
  const trackUIdFromUrl = searchParams.get("trackUId");
  const hasAutoOpenedRef = useRef(false);

  // Modal hooks
  const editDeveloperUserHook = useEditDeveloperUser({ track: trackForEdit });
  const updateStatusHook = useUpdateTrackStatus({ track: trackForEdit });

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const animationTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // Filter tracks based on selected developer
  const filteredTracks = useMemo(() => {
    if (selectedDeveloper === "Overview") {
      return tracks;
    }
    return tracks.filter((track) => track.developerName === selectedDeveloper);
  }, [tracks, selectedDeveloper]);

  // Get the live version of selected track to ensure updates (like status changes) are reflected immediately
  const activeTrack = useMemo(() => {
    if (!selectedTrack) return null;
    return tracks.find((t) => t.id === selectedTrack.id) || selectedTrack;
  }, [tracks, selectedTrack]);

  const handleEditUser = (track: DeveloperTrack) => {
    setTrackForEdit(track);
    editDeveloperUserHook.openModal(track);
  };

  const handleUpdateStatus = (track: DeveloperTrack) => {
    setTrackForEdit(track);
    updateStatusHook.openModal(track);
  };

  const setCardRef = useCallback(
    (trackId: string, element: HTMLDivElement | null) => {
      if (element) {
        cardRefs.current.set(trackId, element);
      } else {
        cardRefs.current.delete(trackId);
      }
    },
    []
  );

  const handleTrackClick = useCallback(
    (track: DeveloperTrack, cardElement: HTMLDivElement | null) => {
      // On mobile, use the parent callback for navigation (no animation)
      if (isMobile && onMobileTrackSelect) {
        onMobileTrackSelect(track);
        return;
      }

      // Desktop behavior with animation
      if (!cardElement || !containerRef.current) return;

      // Get the card's current position relative to viewport
      const cardRect = cardElement.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      setStartPosition({
        top: cardRect.top - containerRect.top,
        left: cardRect.left - containerRect.left,
        width: cardRect.width,
        height: cardRect.height,
      });

      setSelectedTrack(track);
      setIsAnimating(true);

      // Start the animation after a tiny delay to ensure state is set
      requestAnimationFrame(() => {
        setIsDetailViewOpen(true);

        // End animation state after transition completes
        animationTimerRef.current = setTimeout(() => {
          setIsAnimating(false);
        }, 650);
      });
    },
    [isMobile, onMobileTrackSelect]
  );

  const handleCloseDetail = useCallback(() => {
    setIsAnimating(true);
    setIsDetailViewOpen(false);

    // Wait for exit animation, then clean up
    closeTimerRef.current = setTimeout(() => {
      setSelectedTrack(null);
      setStartPosition(null);
      setIsAnimating(false);
    }, 300);
  }, []);

  const handleFilterChipClick = (developer: string) => {
    if (isDetailViewOpen) {
      handleCloseDetail();
    }
    setSelectedDeveloper(developer);
  };

  // Calculate the final position for the card (right side)
  const getFinalPosition = useCallback((): CardPosition => {
    if (!containerRef.current) {
      return { top: 16, left: 0, width: 336, height: 400 };
    }
    const containerRect = containerRef.current.getBoundingClientRect();
    return {
      top: 16,
      left: containerRect.width - 336 - 16,
      width: 336,
      height: 400,
    };
  }, []);

  // Auto-open effect — must be after getFinalPosition is declared
  useEffect(() => {
    if (
      !trackUIdFromUrl ||
      isLoading ||
      !tracks.length ||
      hasAutoOpenedRef.current
    ) {
      return;
    }

    const matchedTrack = tracks.find((t) => t.trackUId === trackUIdFromUrl);
    if (matchedTrack) {
      hasAutoOpenedRef.current = true;

      if (isMobile && onMobileTrackSelect) {
        // On mobile, use the proper mobile track selection flow
        onMobileTrackSelect(matchedTrack);
      } else {
        // Desktop: Set the start position to the final position so the floating card renders immediately
        const finalPos = getFinalPosition();
        setStartPosition(finalPos);
        setSelectedTrack(matchedTrack);
        setIsDetailViewOpen(true);
      }

      // Clean up the URL param
      searchParams.delete("trackUId");
      setSearchParams(searchParams, { replace: true });
    }
  }, [trackUIdFromUrl, isLoading, tracks, searchParams, setSearchParams, getFinalPosition, isMobile, onMobileTrackSelect]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: isMobile ? "auto" : "hidden",
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              backgroundColor: theme.nawy.colors.semantic.layer.primary,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                padding: `${theme.nawy.spacing.md} ${theme.nawy.spacing.md} 0 ${theme.nawy.spacing.md}`,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={theme.nawy.spacing.xxs}
                >
                  <Typography
                    sx={{
                      ...theme.nawy.typography.english.variants.headline.sm,
                      color: theme.nawy.colors.semantic.text.primary,
                    }}
                  >
                    Developer tracks
                  </Typography>
                </Stack>
              </Stack>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: theme.nawy.spacing.sm,
                padding: `${theme.nawy.spacing.sm} ${theme.nawy.spacing.md}`,
                borderBottom: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
                flexWrap: "wrap",
              }}
            >
              <FilterChip
                label="Overview"
                isSelected={true}
                onClick={() => {}}
              />
            </Box>
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            backgroundColor: isMobile
              ? "transparent"
              : theme.nawy.colors.semantic.layer.secondary,
            padding: theme.nawy.spacing.md,
            overflowY: "auto",
          }}
        >
          <DeveloperTrackSkeleton count={isMobile ? 3 : 6} />
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: isMobile ? "auto" : "hidden",
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              backgroundColor: theme.nawy.colors.semantic.layer.primary,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                padding: `${theme.nawy.spacing.md} ${theme.nawy.spacing.md} 0 ${theme.nawy.spacing.md}`,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.headline.sm,
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  Developer tracks
                </Typography>
              </Stack>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: theme.nawy.spacing.sm,
                padding: `${theme.nawy.spacing.sm} ${theme.nawy.spacing.md}`,
                borderBottom: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
              }}
            >
              <FilterChip
                label="Overview"
                isSelected={true}
                onClick={() => {}}
              />
            </Box>
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            backgroundColor: isMobile
              ? "transparent"
              : theme.nawy.colors.semantic.layer.secondary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <EmptyState
            icon={<DeveloperTrackIcon />}
            label="Error loading tracks"
            description="There was an error fetching developer tracks data."
          />
        </Box>
      </Box>
    );
  }

  if (!tracks || tracks.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: isMobile ? "auto" : "hidden",
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              backgroundColor: theme.nawy.colors.semantic.layer.primary,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                padding: `${theme.nawy.spacing.md} ${theme.nawy.spacing.md} 0 ${theme.nawy.spacing.md}`,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.headline.sm,
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  Developer tracks
                </Typography>
              </Stack>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: theme.nawy.spacing.sm,
                padding: `${theme.nawy.spacing.sm} ${theme.nawy.spacing.md}`,
                borderBottom: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
              }}
            >
              <FilterChip
                label="Overview"
                isSelected={true}
                onClick={() => {}}
              />
            </Box>
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            backgroundColor: isMobile
              ? "transparent"
              : theme.nawy.colors.semantic.layer.secondary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? theme.nawy.spacing.md : theme.nawy.spacing.xl,
          }}
        >
          <EmptyState
            icon={<DeveloperTrackIcon width={64} height={64} />}
            label="No developer tracks yet"
            description="This lead doesn't have any developer tracks yet"
            action={
              canAddTrack && (
                <Button
                  variant="contained"
                  startIcon={<Plus size={16} />}
                  onClick={() => setIsCreateModalOpen(true)}
                  sx={{
                    backgroundColor: theme.nawy.colors.semantic.layer.accent,
                    color: theme.nawy.colors.semantic.text.onColor,
                    borderRadius: theme.nawy.radius.lg,
                    textTransform: "none",
                    height: "36px",
                    padding: `${theme.nawy.spacing.xs} ${theme.nawy.spacing.md}`,
                    boxShadow: "none",
                    ...theme.nawy.typography.english.variants.body.md.semibold,
                    "&:hover": {
                      backgroundColor:
                        theme.nawy.colors.semantic.layer.accentDark,
                      boxShadow: "none",
                    },
                  }}
                >
                  New track
                </Button>
              )
            }
          />
        </Box>
        <CreateTrackModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      </Box>
    );
  }

  const finalPosition = getFinalPosition();

  // On mobile, if a track is selected, show only the activity view
  if (isMobile && selectedMobileTrack) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "auto",
        }}
      >
        <TrackActivity track={selectedMobileTrack} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: isMobile ? "auto" : "hidden",
        marginTop: isMobile ? 0 : `-${theme.nawy.spacing.xs}`,
        marginY: isMobile ? 0 : `-${theme.nawy.spacing.xs}`,
      }}
    >
      {/* Header - Hidden on mobile since FullPageModal provides the header */}
      {!isMobile && (
        <Box
          sx={{
            backgroundColor: theme.nawy.colors.semantic.layer.primary,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              padding: isDetailViewOpen
                ? `20px ${theme.nawy.spacing.md}`
                : `${theme.nawy.spacing.md} ${theme.nawy.spacing.md} 0 ${theme.nawy.spacing.md}`,
              borderBottom: isDetailViewOpen
                ? `1px solid ${theme.nawy.colors.semantic.border.primary}`
                : "none",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={theme.nawy.spacing.xs}
              >
                {isDetailViewOpen && (
                  <IconButton
                    onClick={handleCloseDetail}
                    sx={{
                      padding: theme.nawy.spacing.xxs,
                      marginLeft: `-${theme.nawy.spacing.xxs}`,
                      color: theme.nawy.colors.semantic.text.secondary,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor:
                          theme.nawy.colors.semantic.layer.secondary,
                        color: theme.nawy.colors.semantic.text.primary,
                      },
                    }}
                  >
                    <ChevronLeft size={24} />
                  </IconButton>
                )}
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.headline.sm,
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  {isDetailViewOpen && activeTrack
                    ? `${activeTrack.developerName} Track`
                    : "Developer tracks"}
                </Typography>
                {!isDetailViewOpen && (
                  <Typography
                    sx={{
                      ...theme.nawy.typography.english.variants.body.sm.regular,
                      color: theme.nawy.colors.semantic.text.primary,
                      paddingTop: "6px",
                    }}
                  >
                    ({tracks.length} tracks)
                  </Typography>
                )}
              </Stack>

              {/* Only show header button if we have tracks (otherwise empty state shows one) AND we are allowed to add tracks */}
              {!isDetailViewOpen && canAddTrack && tracks.length > 0 && (
                <Button
                  variant="contained"
                  startIcon={<Plus size={16} />}
                  onClick={() => setIsCreateModalOpen(true)}
                  sx={{
                    backgroundColor: theme.nawy.colors.semantic.layer.accent,
                    color: theme.nawy.colors.semantic.text.onColor,
                    borderRadius: theme.nawy.radius.lg,
                    textTransform: "none",
                    height: "36px",
                    padding: `${theme.nawy.spacing.xs} ${theme.nawy.spacing.md}`,
                    boxShadow: "none",
                    ...theme.nawy.typography.english.variants.body.md.semibold,
                    "&:hover": {
                      backgroundColor:
                        theme.nawy.colors.semantic.layer.accentDark,
                      boxShadow: "none",
                    },
                  }}
                >
                  New track
                </Button>
              )}
            </Stack>
          </Box>

          {/* Filter Chips - Only shown on desktop */}
          {!isDetailViewOpen && (
            <Box
              sx={{
                display: "flex",
                gap: theme.nawy.spacing.sm,
                padding: `${theme.nawy.spacing.sm} ${theme.nawy.spacing.md}`,
                borderBottom: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
                flexWrap: "wrap",
              }}
            >
              <FilterChip
                label="Overview"
                isSelected={selectedDeveloper === "Overview"}
                onClick={() => handleFilterChipClick("Overview")}
              />
              {developers.map((developer) => (
                <FilterChip
                  key={developer}
                  label={developer}
                  isSelected={selectedDeveloper === developer}
                  onClick={() => handleFilterChipClick(developer)}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Content Section */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          backgroundColor: isMobile
            ? "transparent"
            : theme.nawy.colors.semantic.layer.secondary,
          overflow: isMobile ? "auto" : "hidden",
          position: "relative",
        }}
      >
        {/* Grid View */}
        <Box
          sx={{
            position: isMobile ? "relative" : "absolute",
            inset: isMobile ? undefined : 0,
            padding: isMobile ? theme.nawy.spacing.md : theme.nawy.spacing.md,
            overflowY: "auto",
            opacity: isDetailViewOpen ? 0 : 1,
            transition: "opacity 0.4s ease",
            pointerEvents: isDetailViewOpen ? "none" : "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.md,
              ...(isMobile
                ? {}
                : {
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(320px, 336px))",
                  }),
            }}
          >
            {filteredTracks.map((track) => (
              <ArchivedOverlay
                key={track.id}
                isArchived={isLeadArchived}
                sx={{ height: "100%" }}
              >
                <Box
                  ref={(el) => setCardRef(track.id, el as HTMLDivElement)}
                  sx={{
                    opacity:
                      isAnimating && selectedTrack?.id === track.id ? 0 : 1,
                    transition: "opacity 0.15s ease",
                    height: "100%",
                  }}
                >
                  <DeveloperTrackCard
                    track={track}
                    onViewDetails={() =>
                      handleTrackClick(
                        track,
                        cardRefs.current.get(track.id) || null
                      )
                    }
                    onEditUser={() => handleEditUser(track)}
                    onUpdateStatus={() => handleUpdateStatus(track)}
                  />
                </Box>
              </ArchivedOverlay>
            ))}
          </Box>
        </Box>

        {/* Animating Card Clone */}
        {selectedTrack &&
          startPosition &&
          (isAnimating || isDetailViewOpen) && (
            <Box
              sx={{
                position: "absolute",
                top: isDetailViewOpen ? finalPosition.top : startPosition.top,
                left: isDetailViewOpen
                  ? finalPosition.left
                  : startPosition.left,
                width: isDetailViewOpen
                  ? finalPosition.width
                  : startPosition.width,
                zIndex: 100,
                transition: isAnimating
                  ? "all 0.55s cubic-bezier(0.4, 0, 0.2, 1)"
                  : "none",
                opacity: isAnimating || isDetailViewOpen ? 1 : 0,
                boxShadow: isAnimating ? theme.nawy.elevation.xl : "none",
              }}
            >
              <DeveloperTrackCard
                track={activeTrack || selectedTrack}
                isDetailView={isDetailViewOpen}
                onViewDetails={handleCloseDetail}
                onEditUser={() => handleEditUser(activeTrack || selectedTrack)}
                onUpdateStatus={() =>
                  handleUpdateStatus(activeTrack || selectedTrack)
                }
              />
            </Box>
          )}

        {/* Activity Section - Left side when detail view is open */}
        <Box
          sx={{
            position: "absolute",
            top: theme.nawy.spacing.md,
            left: theme.nawy.spacing.md,
            right: `calc(336px + ${theme.nawy.spacing.xl})`, // Card width + gap + padding
            bottom: theme.nawy.spacing.md,
            opacity: isDetailViewOpen && !isAnimating ? 1 : 0,
            transform:
              isDetailViewOpen && !isAnimating
                ? "translateX(0)"
                : "translateX(-30px)",
            transition: "opacity 0.25s ease 0.1s, transform 0.25s ease 0.1s",
            pointerEvents: isDetailViewOpen && !isAnimating ? "auto" : "none",
          }}
        >
          {activeTrack && <TrackActivity track={activeTrack} />}
        </Box>
      </Box>

      {/* Modals */}
      <EditDeveloperUserModal
        isOpen={editDeveloperUserHook.isOpen}
        onClose={editDeveloperUserHook.closeModal}
        track={trackForEdit}
        hookValues={editDeveloperUserHook}
      />

      <UpdateStatusModal
        isOpen={updateStatusHook.isOpen}
        onClose={updateStatusHook.closeModal}
        track={trackForEdit}
        hookValues={updateStatusHook}
      />

      <CreateTrackModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </Box>
  );
};

export default DeveloperTracksTab;
